/**
 * Heatmap CSV data loader and utilities
 * 
 * These heatmaps are universal mana models that map land count, nonland count,
 * and mana ratio to deck weight and mana curve profiles.
 */

// Cache for loaded CSV files
const csvCache = new Map<string, string>();

/**
 * Load a heatmap CSV file from public/data
 */
export async function loadHeatmapCSV(deckSize: 40 | 60 | 80 | 99): Promise<string> {
  const cacheKey = `heatmap-${deckSize}`;

  if (csvCache.has(cacheKey)) {
    return csvCache.get(cacheKey)!;
  }

  try {
    const response = await fetch(`/data/deckstr-${deckSize}-card-deck.csv`);
    if (!response.ok) {
      throw new Error(`Failed to load heatmap: ${response.status}`);
    }

    const csv = await response.text();
    csvCache.set(cacheKey, csv);
    return csv;
  } catch (error) {
    console.error(`Error loading ${deckSize}-card heatmap:`, error);
    throw error;
  }
}

/**
 * Parse a heatmap CSV into structured data
 */
export interface HeatmapRow {
  land: number;
  nonland: number;
  ratio: number;
  deckWeight: number;
  manaCurve: number;
}

export function parseHeatmapCSV(csv: string): HeatmapRow[] {
  const lines = csv.trim().split(/\r?\n/);
  const rows: HeatmapRow[] = [];

  // Skip header line
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i]?.trim();
    if (!line) continue;

    const parts = line.split(',');
    if (parts.length !== 5) continue;

    rows.push({
      land: parseInt(parts[0] || '0', 10),
      nonland: parseInt(parts[1] || '0', 10),
      ratio: parseFloat(parts[2] || '0'),
      deckWeight: parseFloat(parts[3] || '0'),
      manaCurve: parseFloat(parts[4] || '0'),
    });
  }

  return rows;
}

/**
 * Find the closest heatmap entry for a given land/nonland/ratio configuration
 */
export function findClosestHeatmapEntry(
  heatmap: HeatmapRow[],
  lands: number,
  nonlands: number,
  targetRatio?: number
): HeatmapRow | null {
  if (heatmap.length === 0) return null;

  let closest: HeatmapRow | null = null;
  let minDistance = Infinity;

  for (const row of heatmap) {
    // Calculate Euclidean distance
    const landDist = row.land - lands;
    const nonlandDist = row.nonland - nonlands;
    const ratioDist = targetRatio ? (row.ratio - targetRatio) : 0;

    const distance = Math.sqrt(
      landDist * landDist +
      nonlandDist * nonlandDist +
      ratioDist * ratioDist
    );

    if (distance < minDistance) {
      minDistance = distance;
      closest = row;
    }
  }

  return closest;
}

/**
 * Get expected deck weight for a configuration
 */
export function getExpectedWeight(
  heatmap: HeatmapRow[],
  lands: number,
  nonlands: number,
  ratio: number
): number | null {
  const entry = findClosestHeatmapEntry(heatmap, lands, nonlands, ratio);
  return entry ? entry.deckWeight : null;
}

/**
 * Determine deck size bucket from total cards
 */
export function getDeckSizeBucket(totalCards: number): 40 | 60 | 80 | 99 {
  if (totalCards >= 35 && totalCards <= 50) return 40;
  if (totalCards >= 51 && totalCards <= 70) return 60;
  if (totalCards >= 71 && totalCards <= 89) return 80;
  return 99;
}

/**
 * Preload all heatmap CSVs (call this on app initialization)
 */
export async function preloadHeatmaps(): Promise<void> {
  try {
    await Promise.all([
      loadHeatmapCSV(40),
      loadHeatmapCSV(60),
      loadHeatmapCSV(80),
      loadHeatmapCSV(99),
    ]);
    console.log('✓ Heatmap CSVs preloaded');
  } catch (error) {
    console.warn('Failed to preload heatmaps:', error);
  }
}

/**
 * Calculate actual ratio from deck configuration
 */
export function calculateActualRatio(
  deckWeight: number,
  nonlands: number
): number {
  return nonlands > 0 ? deckWeight / nonlands : 0;
}

/**
 * Validate deck against heatmap and get recommendations
 */
export interface HeatmapValidationResult {
  status: 'optimal' | 'acceptable' | 'warning' | 'error';
  expectedWeight: number;
  actualWeight: number;
  delta: number;
  deltaPercent: number;
  message: string;
  suggestedLands?: number;
  suggestedRatio?: number;
}

export async function validateDeckAgainstHeatmap(
  totalCards: number,
  lands: number,
  nonlands: number,
  actualWeight: number
): Promise<HeatmapValidationResult> {
  const deckSize = getDeckSizeBucket(totalCards);

  try {
    const csv = await loadHeatmapCSV(deckSize);
    const heatmap = parseHeatmapCSV(csv);
    const actualRatio = calculateActualRatio(actualWeight, nonlands);

    const closest = findClosestHeatmapEntry(heatmap, lands, nonlands, actualRatio);

    if (!closest) {
      return {
        status: 'error',
        expectedWeight: 0,
        actualWeight,
        delta: 0,
        deltaPercent: 0,
        message: 'No matching heatmap entry found',
      };
    }

    const delta = actualWeight - closest.deckWeight;
    const deltaPercent = (delta / closest.deckWeight) * 100;

    let status: HeatmapValidationResult['status'];
    let message: string;

    if (Math.abs(deltaPercent) < 5) {
      status = 'optimal';
      message = `Deck weight is optimal for ${deckSize}-card format`;
    } else if (Math.abs(deltaPercent) < 10) {
      status = 'acceptable';
      message = `Deck weight is acceptable (${deltaPercent > 0 ? 'slightly heavy' : 'slightly light'})`;
    } else if (Math.abs(deltaPercent) < 20) {
      status = 'warning';
      message = `Deck weight is ${deltaPercent > 0 ? 'too heavy' : 'too light'} for optimal play`;
    } else {
      status = 'error';
      message = `Deck weight is significantly ${deltaPercent > 0 ? 'too heavy' : 'too light'}`;
    }

    return {
      status,
      expectedWeight: closest.deckWeight,
      actualWeight,
      delta,
      deltaPercent,
      message,
      suggestedLands: closest.land !== lands ? closest.land : undefined,
      suggestedRatio: closest.ratio !== actualRatio ? closest.ratio : undefined,
    };
  } catch (error) {
    return {
      status: 'error',
      expectedWeight: 0,
      actualWeight,
      delta: 0,
      deltaPercent: 0,
      message: `Failed to validate: ${error}`,
    };
  }
}

/**
 * Clear the cache (useful for testing or memory management)
 */
export function clearHeatmapCache(): void {
  csvCache.clear();
}

